import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask } from '@pijma/input'

import { Dispatch, RootState, store } from '@stores'
import { TPayFormState } from '@stores/types/*'
import { charIsEnSymbol, replaceNotEnSymbol } from '@utils'
import styled from 'styled-components'

import { SESSION_SENDER_FULL_NAME_KEY } from '../../../../../stores/payform/payForm'
import { TPaymentGateway } from '../../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../../common/header/Headings'
import { Island } from '../../../../common/island'
import NavigationBar from '../../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 0;
  margin-bottom: 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 20px 20px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  paymentGateway?: TPaymentGateway
  paymentBlocking?: boolean
}

interface DispatchProps {
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const SellSourceFullNamePage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [formValid, setFormValid] = useState<boolean>(false)

  useEffect(() => {
    setFormValid(
      props.payForm.senderFullName?.firstName !== undefined &&
        props.payForm.senderFullName?.lastName !== undefined &&
        props.payForm.senderFullName?.lastName !== '' &&
        props.payForm.senderFullName?.firstName !== '',
    )
  }, [
    props.payForm.senderFullName?.firstName,
    props.payForm.senderFullName?.lastName,
  ])

  // Pijma input masks
  const firstNameMask = useBaseMask({
    type: 'text',
    format: replaceNotEnSymbol,
    isValidInputCharacter: charIsEnSymbol,
    removeFormatting: replaceNotEnSymbol,
    value: props.payForm.senderFullName?.firstName,
    onValueChange: ({ formattedValue }) => {
      props.setFirstName(formattedValue)
    },
  })
  const lastNameMask = useBaseMask({
    type: 'text',
    format: replaceNotEnSymbol,
    isValidInputCharacter: charIsEnSymbol,
    removeFormatting: replaceNotEnSymbol,
    value: props.payForm.senderFullName?.lastName,
    onValueChange: ({ formattedValue }) => {
      props.setLastName(formattedValue)
    },
  })

  const goToMethodPage = () => {
    if (props.payForm.senderFullName) {
      sessionStorage.setItem(
        SESSION_SENDER_FULL_NAME_KEY,
        JSON.stringify(props.payForm.senderFullName),
      )
    }
    if (props.payForm.paymentGateway?.currencyTo) redirectTo(`../../method`)
  }

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentDisabled
          sumDisabled
          showMethod
          methodDisabled
          noPaymentMethod
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle data-testid="userdata.header">
          <H4>{intl.formatMessage({ id: 'sellSourceFullNamePage.title' })}</H4>
          <div style={{ marginTop: '4px' }}>
            <CaptionText500>
              {intl.formatMessage({ id: 'sellSourceFullNamePage.subtitle' })}
            </CaptionText500>
          </div>
        </SourceTitle>
        <InputContainer>
          <Input
            {...lastNameMask}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            description={intl.formatMessage({
              id: 'sellSourceFullNamePage.description',
            })}
            placeholder={undefined}
            label={intl.formatMessage({
              id: 'sellSourceFullNamePage.lastNameLabel',
            })}
            allowClear={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...firstNameMask}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            placeholder={intl.formatMessage({
              id: 'sellSourceFullNamePage.firstNamePlaceholder',
            })}
            allowClear={true}
          />
        </InputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToMethodPage()}
          >
            {intl.formatMessage({ id: 'sellSourceFullNamePage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer
        onClick={() => redirectTo('../../provider-select')}
        data-testid="payment-methods.back"
      >
        <BodyText500>
          {intl.formatMessage({ id: 'sellSourceFullNamePage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateway: state.payForm?.paymentGateway,
  paymentBlocking: state.payForm?.paymentBlocking,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setFirstName(firstName: string): void {
      const current = store.getState().payForm.senderFullName

      dispatch.payForm.set({
        senderFullName: {
          firstName: firstName,
          patronymic: current?.patronymic,
          lastName: current?.lastName ?? '',
        },
      })
    },
    setLastName(lastName: string): void {
      const current = store.getState().payForm.senderFullName

      dispatch.payForm.set({
        senderFullName: {
          firstName: current?.firstName ?? '',
          patronymic: current?.patronymic,
          lastName: lastName,
        },
      })
    },
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SellSourceFullNamePage)
